// ─── Hook : DONNÉES des plans & emplacements ──────────────────────
// Extrait d'EcranPlans (découpage). Écoute les plans accessibles + les
// inventaires/items (agrégation des objets rangés), migration douce de
// l'ancien arbre `emplacements/{tenant}`, CRUD de plan (SelecteurPlan) et
// dérivés : comptages par lieu, contenu groupé par élément (direct/récursif).
import { useEffect, useMemo, useRef, useState } from 'react';
import { useAuth, useEspace, useT, useQuotaElements, usePrendreJeton } from '@mop/core';
import {
  ecouterInventairesAccessibles, ecouterItems, lireEmplacementsUneFois,
  idsDescendants, articlesDe, photosArticle,
} from '../services/inventaires';
import {
  ecouterPlansAccessibles, creerPlan, renommerPlan, modifierEmojiPlan, supprimerPlan,
} from '../services/plansData';
import { compterDirect, compterTotaux } from '../services/plan';

export default function useDonneesPlans({ edit }) {
  const t = useT();
  const { tenantRangement, tenantIds, espaceActif, assurerEspace } = useEspace();
  const prendreJeton = usePrendreJeton();
  const { user } = useAuth();
  const quota = useQuotaElements();
  const [plans, setPlans]      = useState([]);
  const [plansPrets, setPP]    = useState(false);
  const [planSelId, setPSI]    = useState(null);
  const [inventaires, setInv]  = useState([]);
  const [itemsParInv, setIPI]  = useState({});
  const [err, setErr]          = useState('');

  const planCourant = useMemo(() => plans.find(p => p.id === planSelId) || null, [plans, planSelId]);
  const emplacements = useMemo(() => planCourant?.arbre || [], [planCourant]);
  const planEff = planCourant?.id || null;

  // Création / renommage / suppression d'un plan (pilotés par <SelecteurPlan/>).
  const creerPlanUI = async ({ nom, emoji }) => {
    // Un plan est un ELEMENT comme un autre : il consomme le meme quota que les
    // calendriers et les inventaires.
    if (!quota.peutCreer) { setErr(t('jetons.creationBloquee', { n: quota.possedes, max: quota.max })); return; }
    try {
      // Espace cree au nom de l'utilisateur s'il n'en possede aucun.
      const espaceId = espaceActif?.id || await assurerEspace();
      const id = await creerPlan({
        espaceId, nom, emoji, arbre: [],
        creePar: user?.uid, creeParNom: user?.prenom || user?.email || '',
        ownerUid: espaceActif?.creePar || user?.uid,
      });
      setPSI(id);
      // Un plan cree se gere : il prend son jeton ⚙️.
      if (id) await prendreJeton(`plan:${id}`);
    } catch (e) { setErr(e?.message || String(e)); }
  };
  const renommerPlanUI = async (id, { nom, emoji }) => {
    try { await renommerPlan(id, nom); await modifierEmojiPlan(id, emoji); }
    catch (e) { setErr(e?.message || String(e)); }
  };
  const supprimerPlanUI = async (id) => {
    try { await supprimerPlan(id); if (planSelId === id) setPSI(null); }
    catch (e) { setErr(e?.message || String(e)); }
  };

  // Plans accessibles (par `acces`, à travers tous les espaces).
  useEffect(() => {
    if (!user?.uid) { setPlans([]); setPP(true); return undefined; }
    setPP(false);
    return ecouterPlansAccessibles(
      user.uid,
      (l) => { setPlans(l); setPP(true); },
      (e) => { setPP(true); setErr(e?.message || String(e)); },
    );
  }, [user?.uid]);

  // Inventaires accessibles (pour agréger les objets rangés dans les plans).
  useEffect(() => {
    if (!user?.uid) { setInv([]); return undefined; }
    return ecouterInventairesAccessibles(user.uid, setInv, (e) => setErr(e?.message || String(e)));
  }, [user?.uid]);

  // Plan actif par défaut : le 1er accessible (ou garde le choix s'il est encore valide).
  useEffect(() => {
    if (!plansPrets) return;
    if (planSelId && plans.some(p => p.id === planSelId)) return;
    setPSI(plans[0]?.id || null);
  }, [plans, plansPrets, planSelId]);

  // Migration douce (une fois par espace) : si l'espace possède un ancien arbre
  // `emplacements/{tenant}` mais AUCUN plan, on crée un plan « par défaut » qui le
  // reprend (mêmes ids → liens articles préservés). Non destructif.
  const migreRef = useRef(new Set());
  useEffect(() => {
    if (!edit || !plansPrets || !user?.uid || !espaceActif) return;
    if (!tenantRangement && !(tenantIds || []).length) return;
    if (migreRef.current.has(espaceActif.id)) return;
    if (plans.some(p => p.espaceId === espaceActif.id)) return;
    migreRef.current.add(espaceActif.id);
    (async () => {
      try {
        // On cherche le 1er tenant qui a RÉELLEMENT un arbre, au lieu de faire
        // confiance à `tenantRangement` (= tenantsHerites[0]) : en prod, le 1er
        // hérité était un calendrier au doc `emplacements` VIDE, donc la
        // migration lisait 0 nœud et ne faisait rien — l'arbre de rangement est
        // resté invisible dans la suite. Constaté le 08/08 (cf. COWORK).
        const candidats = [...new Set([tenantRangement, ...(tenantIds || [])].filter(Boolean))];
        let arbre = [];
        for (const tid of candidats) {
          const a = await lireEmplacementsUneFois(tid);
          if (Array.isArray(a) && a.length) { arbre = a; break; }
        }
        if (Array.isArray(arbre) && arbre.length) {
          const id = await creerPlan({
            espaceId: espaceActif.id, nom: espaceActif.nom || 'Maison', emoji: espaceActif.emoji || '🏠',
            arbre, creePar: user.uid, creeParNom: user.prenom || user.email || '',
            ownerUid: espaceActif.creePar || user.uid,
          });
          setPSI(id);
        }
      } catch { /* silencieux : la migration retentera au prochain montage */ }
    })();
  }, [edit, plansPrets, plans, espaceActif?.id, tenantRangement, (tenantIds || []).join(','), user?.uid]); // eslint-disable-line react-hooks/exhaustive-deps

  const invIds = useMemo(() => inventaires.map(i => i.id), [inventaires]);
  const invIdsKey = invIds.join(',');
  useEffect(() => {
    if (!invIds.length) { setIPI({}); return undefined; }
    const unsubs = invIds.map(id => ecouterItems(id, its => setIPI(m => ({ ...m, [id]: its }))));
    return () => unsubs.forEach(u => u && u());
  }, [invIdsKey]); // eslint-disable-line react-hooks/exhaustive-deps

  // Comptage : total (direct + sous-lieux) par emplacement.
  const tousArticles = useMemo(() => {
    const out = [];
    Object.values(itemsParInv).forEach(its => (its || []).forEach(it => articlesDe(it).forEach(a => out.push(a))));
    return out;
  }, [itemsParInv]);
  const totaux = useMemo(() => compterTotaux(emplacements, compterDirect(tousArticles)), [emplacements, tousArticles]);
  const compteDe = (id) => totaux.get(id) || 0;

  // Contenu d'un lieu, groupé par élément.
  const contenuParEmp = useMemo(() => {
    const m = new Map();
    Object.entries(itemsParInv).forEach(([invId, its]) => (its || []).forEach(it => articlesDe(it).forEach(a => {
      if (!a.emplacementId) return;
      let byItem = m.get(a.emplacementId);
      if (!byItem) { byItem = new Map(); m.set(a.emplacementId, byItem); }
      let g = byItem.get(it.id);
      if (!g) { g = { id: it.id, invId, item: it, nom: it.nom, emoji: it.emoji || '📦', arts: [] }; byItem.set(it.id, g); }
      g.arts.push({ id: a.id, nom: a.nom || it.nom, qte: Number(a.qte) || 0, unique: !!a.unique, photo: photosArticle(a)[0] || null });
    })));
    return m;
  }, [itemsParInv]);
  const elementsDe = (empId) => Array.from((contenuParEmp.get(empId) || new Map()).values());

  // Éléments contenus dans un lieu ET tous ses sous-lieux (récursif), groupés
  // par élément — chaque article porte son emplacement précis (pour le 📍).
  function elementsSousLieu(empId) {
    const ids = new Set(idsDescendants(emplacements, empId));
    const m = new Map();
    Object.values(itemsParInv).forEach(its => (its || []).forEach(it => articlesDe(it).forEach(a => {
      if (!a.emplacementId || !ids.has(a.emplacementId)) return;
      let g = m.get(it.id);
      if (!g) { g = { id: it.id, item: it, nom: it.nom, emoji: it.emoji || '📦', arts: [] }; m.set(it.id, g); }
      g.arts.push({ id: a.id, nom: a.nom || it.nom, qte: Number(a.qte) || 0, unique: !!a.unique,
        photo: photosArticle(a)[0] || null, emplacementId: a.emplacementId });
    })));
    return Array.from(m.values());
  }

  return {
    plans, plansPrets, planSelId, setPSI, planCourant, emplacements, planEff,
    inventaires, itemsParInv, err, setErr,
    creerPlanUI, renommerPlanUI, supprimerPlanUI, quota,
    totaux, compteDe, contenuParEmp, elementsDe, elementsSousLieu,
  };
}
